import {
  canUseMjForItem,
  getStoredAuthUser,
  hasAccessConfig,
  isMjForItem,
  isUniverseAdmin,
  isUserInList,
} from './auth';
import { isGlobalAdmin } from './roles';
import { getStorageValue, setStorageValue } from './userStorage';

export const validPassword = (item, campaign, world, universe) => {
  const authUser = getStoredAuthUser();
  const authUserId = authUser?._id;
  if (
    item?.type === 'universe' &&
    hasAccessConfig(item) &&
    isUniverseAdmin(item, authUserId)
  ) {
    return true;
  }

  const password = getStorageValue('password');
  let passwords = [];
  const strPasswords = getStorageValue('passwords');
  if (strPasswords === null) passwords = [];
  try {
    passwords = JSON.parse(strPasswords).map(
      password => password.hidden !== true && password.password,
    );
  } catch (e) {
    passwords = [];
  }

  const parentCampaign = item?.type === 'campaign' ? null : campaign;

  const hasDirectMatch = value =>
    Boolean(password) && Boolean(value) && password === value;

  const isValidChild = () => {
    return (
      hasDirectMatch(parentCampaign?.password) ||
      passwords.includes(parentCampaign?.password) ||
      (parentCampaign && !parentCampaign?.password) ||
      hasDirectMatch(world?.password) ||
      passwords.includes(world?.password) ||
      hasDirectMatch(universe?.password) ||
      passwords.includes(universe?.password)
    );
  };
  if (!password && !strPasswords) return false;

  if (!item?.type) {
    if (item?.campaign === password) {
      setStorageValue('campaign', password);
      return true;
    }
    if (item?.world === password) {
      setStorageValue('world', password);
      return true;
    }
    if (item?.universe === password) {
      setStorageValue('universe', password);
      return true;
    }
  }
  if (
    isValidChild() ||
    hasDirectMatch(item?.password) ||
    passwords.includes(item?.password)
  ) {
    return true;
  }

  if (item?.type && password === item?._id) {
    setStorageValue(item?.type, password);
    return true;
  }
  if (getStorageValue(item?.type) === item?._id) {
    return true;
  }

  return false;
};

const getAccessDecision = (entity, userId, universeForAdmin) => {
  if (!entity || !hasAccessConfig(entity)) {
    return null;
  }
  if (entity.type === 'universe') {
    return isUniverseAdmin(entity, userId);
  }
  if (entity.type === 'campaign') {
    const visibility = entity?.access?.visibility || 'public';
    if (visibility === 'public') {
      if (entity?.password) {
        return null;
      }
      return true;
    }
    if (isUniverseAdmin(universeForAdmin, userId)) {
      return true;
    }
    if (isUserInList(entity?.access?.admins, userId)) return true;
    if (isUserInList(entity?.access?.editors, userId)) return true;
    if (isUserInList(entity?.access?.readers, userId)) return true;
    return false;
  }
  return null;
};

const normalizeDefaultRole = role => {
  if (role === 'editor') {
    return 'editor';
  }
  if (role === 'read') {
    return 'reader';
  }
  return 'reader';
};

const isValidObjectId = id => typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);

export const hasResolvedPermissionScope = (item, campaign, world, universe) => {
  if (!item) return false;
  return (
    (!isValidObjectId(item?.campaign) || Boolean(campaign)) &&
    (!isValidObjectId(item?.world) || Boolean(world)) &&
    (!isValidObjectId(item?.universe) || Boolean(universe))
  );
};

export const canAccessItem = (item, campaign, world, universe) => {
  if (!item) return false;
  if (isGlobalAdmin()) return true;
  const authUser = getStoredAuthUser();
  const userId = authUser?._id;
  const universeAdmin = isUniverseAdmin(universe, userId);

  const campaignScope = item?.type === 'campaign' ? null : campaign;

  if (item?.type === 'universe') {
    const accessDecision = getAccessDecision(item, userId, item);
    if (accessDecision === true) return true;
    if (accessDecision === false) {
      return item?.password
        ? validPassword(item, campaignScope, world, universe)
        : false;
    }
    return validPassword(item, campaignScope, world, universe);
  }

  if (item?.type === 'campaign') {
    const accessDecision = getAccessDecision(item, userId, universe);
    if (accessDecision === true) return true;
    if (accessDecision === false) {
      return item?.password
        ? validPassword(item, campaignScope, world, universe)
        : false;
    }
    if (universeAdmin) {
      return true;
    }
    if (!item?.password) {
      return true;
    }
    return validPassword(item, campaignScope, world, universe);
  }

  if (universeAdmin) {
    return true;
  }

  if (campaign) {
    const accessDecision = getAccessDecision(campaign, userId, universe);
    if (accessDecision === true) {
      return true;
    }
    if (accessDecision === false) {
      return campaign?.password
        ? validPassword(item, campaignScope, world, universe)
        : false;
    }
    if (!campaign?.password) {
      return true;
    }
  }

  if (universe) {
    const accessDecision = getAccessDecision(universe, userId, universe);
    if (accessDecision !== null) {
      return accessDecision;
    }
  }

  return validPassword(item, campaignScope, world, universe);
};

export const canOpenItem = (item, campaign, world, universe, items) => {
  if (!item) return false;
  if (!hasResolvedPermissionScope(item, campaign, world, universe)) {
    return false;
  }
  if (canBypassReadOnlyForItem(item, items)) {
    return true;
  }
  return canAccessItem(item, campaign, world, universe);
};

export const getEffectiveRoleForItem = (item, items) => {
  if (!item) return null;
  if (isGlobalAdmin()) return 'admin';
  const authUser = getStoredAuthUser();
  const userId = authUser?._id;
  const universe = item?.type === 'universe' ? item : items?.[item?.universe];
  if (isUniverseAdmin(universe, userId)) return 'admin';

  const campaign =
    item?.type === 'campaign' ? item : (items?.[item?.campaign] ?? null);

  if (campaign && userId) {
    if (isUserInList(campaign?.access?.admins, userId)) return 'admin';
    if (isUserInList(campaign?.access?.editors, userId)) return 'editor';
    if (isUserInList(campaign?.access?.readers, userId)) return 'reader';
  }
  if (campaign?.access?.visibility === 'public') {
    return normalizeDefaultRole(campaign?.access?.defaultRole);
  }

  const world = item?.world ? items?.[item.world] : undefined;
  const campaignScope = item?.type === 'campaign' ? null : campaign;
  if (validPassword(item, campaignScope, world, universe)) {
    return 'editor';
  }

  return null;
};

export const canEditItemForItem = (item, items) => {
  const role = getEffectiveRoleForItem(item, items);
  return role === 'admin' || role === 'editor';
};

export const canBypassReadOnlyForItem = (item, items) => {
  return isMjForItem(item, items) || canUseMjForItem(item, items);
};

export const isTabCampaignAccessible = (tab, items, listCampaignsId = []) => {
  if (!tab) {
    return true;
  }
  if (canBypassReadOnlyForItem(tab, items)) {
    return true;
  }
  const campaignId = tab?.campaign;
  if (
    !campaignId ||
    campaignId === 'non défini' ||
    campaignId === process.env.REACT_APP_NONE_VISITOR
  ) {
    return true;
  }
  if (Array.isArray(listCampaignsId) && listCampaignsId.some(Boolean)) {
    return listCampaignsId.includes(campaignId);
  }
  const campaign = items?.[campaignId];
  if (!campaign) {
    return true;
  }
  const universe = campaign?.universe ? items?.[campaign.universe] : undefined;
  if (canAccessItem(campaign, campaign, undefined, universe)) {
    return true;
  }
  const world = tab?.world ? items?.[tab.world] : undefined;
  const tabUniverse = tab?.universe ? items?.[tab.universe] : undefined;
  return validPassword(tab, campaign, world, tabUniverse);
};

export const isItemCampaignAccessible = (item, listCampaignsId = []) => {
  if (!item) {
    return false;
  }
  const campaignId = item?.campaign;
  if (
    !campaignId ||
    campaignId === 'non défini' ||
    campaignId === process.env.REACT_APP_NONE_VISITOR
  ) {
    return true;
  }
  if (Array.isArray(listCampaignsId) && listCampaignsId.some(Boolean)) {
    return listCampaignsId.includes(campaignId);
  }
  return true;
};

export const canDisplayLinkedItem = ({
  item,
  campaign,
  world,
  universe,
  items,
  listCampaignsId,
  hasMissingAccessScope = false,
}) => {
  return Boolean(
    canBypassReadOnlyForItem(item, items) ||
    (isItemCampaignAccessible(item, listCampaignsId) &&
      !hasMissingAccessScope &&
      canOpenItem(item, campaign, world, universe, items))
  );
};

export const canDisplayNavigationItem = ({
  item,
  campaign,
  world,
  universe,
  items,
}) => {
  return Boolean(
    canOpenItem(item, campaign, world, universe, items) ||
    canBypassReadOnlyForItem(item, items)
  );
};

export const canOpenFolder = ({ folder, universe, items, isAdminOfUniverse }) => {
  return (
    canAccessItem(folder, folder, null, universe) ||
    isMjForItem(folder, items) ||
    isAdminOfUniverse === true
  );
};

export const getCanUseMjForDisplayItem = ({ item, items, isUniverseAdmin }) => {
  return (
    isGlobalAdmin() ||
    isMjForItem(item, items) ||
    isUniverseAdmin ||
    canUseMjForItem(item, items)
  );
};
